import { effect } from "@preact/signals";
import * as preact from "preact";

import * as analytics from "analytics";
import * as premiumUpsellModal from "premiumUpsellModal";
import * as languageMap from "shared/languageMap";
import { cn } from "shared/utils";
import type { GuideSection } from "stores/GuideStore";
import stores from "stores/stores";
import * as guideEntryPopUpView from "views/GuideEntryPopUpView";

const formatNumber = (value: number) => value.toLocaleString("en");

/**
 * Scrolls the guide pane (the sidebar tab's own scroll container) to show the
 * given element. Deliberately not scrollIntoView: that also scrolls every
 * scrollable ancestor, and while the sidebar is closed the pane is still
 * mounted just off-screen to the right, so scrollIntoView drags the whole
 * page horizontally into the blank space beside the text (#entirePage is
 * programmatically scrollable despite overflow hidden).
 */
const scrollGuidePaneToElement = (
  element: HTMLElement,
  options: { center?: boolean; smooth?: boolean } = {}
) => {
  const pane = element.closest<HTMLElement>(".tabContent");
  if (!pane) {
    return;
  }
  const paneRect = pane.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  let top: number;
  if (options.center) {
    top =
      pane.scrollTop +
      elementRect.top -
      paneRect.top -
      (pane.clientHeight - elementRect.height) / 2;
  } else if (elementRect.top < paneRect.top) {
    top = pane.scrollTop + elementRect.top - paneRect.top;
  } else if (elementRect.bottom > paneRect.bottom) {
    top = pane.scrollTop + elementRect.bottom - paneRect.bottom;
  } else {
    // Already fully visible
    return;
  }
  pane.scrollTo({
    top: Math.max(0, top),
    behavior: options.smooth ? "smooth" : "auto",
  });
};

/** Nonsense placeholder rendered blurred instead of a hidden section's text */
const REDACTED_PLACEHOLDER =
  "No peeking! This part of the story is still ahead of you. Keep reading and " +
  "this summary will unlock when you get there. " +
  "No peeking! This part of the story is still ahead of you.";

const GuideExplanation = () => (
  <>
    <h3>Reading guide</h3>
    <p>
      Generate an AI guide for this text: helpful background to read before you
      start, a glossary of the key characters, places and things — click any of
      them in the text to see who's who — and a section-by-section plot summary
      that unlocks as you read, so you can check you didn't miss anything
      without fear of spoilers.
    </p>
  </>
);

/**
 * The button to generate a guide (or replace an outdated one) in the current
 * language, or the notice explaining why the user can't
 */
const GenerateGuide = () => {
  const { allowance, errorMessage, outdated } = stores.guide.state.value;
  const totalWords = Number(stores.bookModel.book.value.totalWords ?? 0);

  if (!allowance) {
    return null;
  }

  if (!allowance.isPremium) {
    return (
      <button
        class="payButton"
        onClick={() => {
          premiumUpsellModal.show({
            title: "guideUpsell",
            heading: "Upgrade to generate Reading Guides",
            subheading:
              "Follow every character and plot thread with AI reading " +
              "guides, plus all the other benefits of Readlang Premium.",
            showTitle: false,
          });
        }}
      >
        Upgrade to generate {outdated ? "an updated" : "a"} guide
      </button>
    );
  }

  if (totalWords > allowance.maxWords) {
    return (
      <p class="guideNotice">
        Sorry, this text is too long for a guide — the limit is{" "}
        {formatNumber(allowance.maxWords)} words.
      </p>
    );
  }

  const remaining = Math.max(0, allowance.limit - allowance.used);
  if (allowance.chargeWords > remaining) {
    return (
      <p class="guideNotice">
        You don't have enough Reading Guide allowance left this month: this text
        needs {formatNumber(allowance.chargeWords)} words but you have{" "}
        {formatNumber(remaining)} of your monthly{" "}
        {formatNumber(allowance.limit)} remaining. Your allowance resets at the
        start of next month.
      </p>
    );
  }

  return (
    <>
      <p class="guideAllowanceEstimate">
        This will use {formatNumber(allowance.chargeWords)} words of your
        monthly Reading Guide allowance ({formatNumber(remaining)} of{" "}
        {formatNumber(allowance.limit)} remaining).
      </p>
      {errorMessage && <p class="guideNotice">{errorMessage}</p>}
      <button
        class="payButton"
        onClick={() => {
          analytics.trackEventWithProperties("generateGuide", {
            bookID: stores.guide.state.value.bookID,
            language: stores.guide.state.value.language,
            words: allowance.chargeWords,
            outdated,
          });
          stores.guide.generate();
        }}
      >
        {outdated ? "Generate an updated guide" : "Generate Guide"}
      </button>
    </>
  );
};

/**
 * Lets the reader choose the guide's language: the language they know, the
 * text's own language (for a guide in the language they're learning), or any
 * other language the text already has a guide in. Each language's guide is
 * kept, so switching back and forth costs nothing once generated.
 */
const GuideLanguagePicker = () => {
  const { bookID, language, languages = [] } = stores.guide.state.value;
  const options = [
    ...new Set([
      language,
      stores.user.state.value.firstLanguage,
      stores.bookModel.book.value.language,
      ...languages,
    ]),
  ].filter((code): code is string => Boolean(code && languageMap.map[code]));
  if (!bookID || !language || options.length < 2) {
    return null;
  }
  return (
    <label class="guideLanguagePicker">
      Guide language:{" "}
      <select
        value={language}
        onChange={(event) =>
          stores.guide.loadForBook(bookID, event.currentTarget.value)
        }
      >
        {options.map((code) => (
          <option key={code} value={code}>
            {languageMap.map[code].name}
          </option>
        ))}
      </select>
    </label>
  );
};

const GuideProgress = () => {
  const progress = stores.guide.state.value.progress ?? 0;
  return (
    <>
      <h3>Generating your guide…</h3>
      <p>
        Reading the whole text and writing your glossary and plot summary. This
        usually takes a minute or two — feel free to keep reading.
      </p>
      <div class="guideProgressBar">
        <div
          class="guideProgressBarFill"
          style={{ width: `${Math.round(Math.max(0.03, progress) * 100)}%` }}
        />
      </div>
    </>
  );
};

/**
 * The section's range through the book as percentages, e.g. "23% - 29%".
 * Positions are inclusive word indexes, so the end uses toPosition + 1 and
 * adjacent sections share their boundary percentage.
 */
const sectionRangeLabel = (section: GuideSection, totalWords: number) =>
  totalWords > 0
    ? `${Math.round((section.fromPosition / totalWords) * 100)}% - ` +
      `${Math.round(((section.toPosition + 1) / totalWords) * 100)}%`
    : undefined;

class GuideSectionView extends preact.Component<{
  section: GuideSection;
  index: number;
  rangeLabel?: string;
  revealed: boolean;
  reading: boolean;
  elementRef?: (element: HTMLElement | null) => void;
}> {
  render() {
    const { section, index, rangeLabel, revealed, reading } = this.props;
    return (
      <div
        ref={this.props.elementRef}
        class={cn({ guideSection: true, redacted: !revealed })}
      >
        <h4>
          {revealed ? section.title || `Section ${index + 1}` : " "}
          {rangeLabel && <span class="guideSectionRange">{rangeLabel}</span>}
          {reading && <span class="guideReadingNow">Reading now</span>}
        </h4>
        {revealed ? (
          <p>{section.summary}</p>
        ) : (
          <div
            class="guideRedactedSummary"
            title="Hidden to avoid spoilers"
            onClick={() => stores.guide.revealSection(index)}
          >
            <p aria-hidden="true">{REDACTED_PLACEHOLDER}</p>
            <button>🔒 Beyond your reading position — click to reveal</button>
          </div>
        )}
      </div>
    );
  }
}

export default class GuideView extends preact.Component {
  private entryElements = new Map<number, HTMLElement>();
  private disposeSelectionEffect?: () => void;
  private readingSectionElement: HTMLElement | null = null;
  private scrolledToReadingSection = false;

  componentDidMount() {
    this.disposeSelectionEffect = effect(() => {
      // Subscribe to every selection, even a repeat of the same entry
      stores.guide.selectionSequence.value;
      const index = stores.guide.selectedEntryIndex.value;
      if (index === undefined) {
        return;
      }
      // Wait for the render triggered by the same signal change
      window.setTimeout(() => {
        const element = this.entryElements.get(index);
        if (element) {
          scrollGuidePaneToElement(element, { smooth: true });
        }
      }, 0);
    });
    this.scrollToReadingSection();
  }

  componentDidUpdate() {
    // Also covers a generation finishing while the tab is already open
    this.scrollToReadingSection();
  }

  componentWillUnmount() {
    this.disposeSelectionEffect?.();
  }

  /** Jumps to the section being read, once, when the tab opens mid-book */
  private scrollToReadingSection() {
    if (this.scrolledToReadingSection || !this.readingSectionElement) {
      return;
    }
    this.scrolledToReadingSection = true;
    scrollGuidePaneToElement(this.readingSectionElement, { center: true });
  }

  render() {
    const { status, guide, outdated } = stores.guide.state.value;

    if (status === "unavailable" || status === "unloaded") {
      return (
        <div class="guideView">
          <h3>Reading guide</h3>
          <p>Sign in to generate an AI glossary and plot summary.</p>
        </div>
      );
    }

    if (status === "loading") {
      return <div class="guideView" />;
    }

    if (status === "none") {
      return (
        <div class="guideView">
          <GuideExplanation />
          <GuideLanguagePicker />
          <GenerateGuide />
        </div>
      );
    }

    if (status === "generating") {
      return (
        <div class="guideView">
          <GuideProgress />
        </div>
      );
    }

    if (status === "error" || !guide) {
      return (
        <div class="guideView">
          <h3>Reading guide</h3>
          <p class="guideNotice">
            {stores.guide.state.value.errorMessage ??
              "Something went wrong while generating the guide."}
          </p>
          <GuideLanguagePicker />
          <button class="payButton" onClick={() => stores.guide.generate()}>
            Try again
          </button>
        </div>
      );
    }

    const selectedEntryIndex = stores.guide.selectedEntryIndex.value;
    const readTo = Number(stores.bookModel.book.value.userBook?.readTo ?? 0);
    const totalWords = Number(stores.bookModel.book.value.totalWords ?? 0);

    this.entryElements.clear();

    return (
      <div class="guideView">
        {outdated && (
          <div class="guideOutdated">
            <p class="guideNotice">
              This guide is based on an earlier version of the text, so parts of
              it may no longer match.
            </p>
            <GenerateGuide />
          </div>
        )}
        {guide.background && (
          <>
            <h3>Background</h3>
            <p class="guideHint">
              Helpful context before you start — no spoilers.
            </p>
            {guide.background.split(/\n+/).map((paragraph, index) => (
              <p key={index} class="guideBackground">
                {paragraph}
              </p>
            ))}
          </>
        )}

        <h3>Plot summary</h3>
        <p class="guideHint">
          Unlocks as you read, so you can check your understanding without
          spoilers.
        </p>
        {guide.sections.map((section, index) => {
          const reading =
            readTo >= section.fromPosition && readTo <= section.toPosition;
          return (
            <GuideSectionView
              key={section.fromPosition}
              section={section}
              index={index}
              rangeLabel={sectionRangeLabel(section, totalWords)}
              revealed={stores.guide.isSectionRevealed(section, index, readTo)}
              reading={reading}
              elementRef={
                // Auto-scroll target; skip when still on the first section
                reading && index > 0
                  ? (element) => {
                      this.readingSectionElement = element;
                    }
                  : undefined
              }
            />
          );
        })}

        <h3>Glossary</h3>
        <p class="guideHint">
          Click any of these names in the text to see its entry.
        </p>
        {guide.glossary.map((entry, index) => (
          <div
            key={entry.term}
            class={cn({
              guideEntry: true,
              selected: index === selectedEntryIndex,
            })}
            ref={(element) => {
              if (element) {
                this.entryElements.set(index, element);
              }
            }}
          >
            <h4>
              <guideEntryPopUpView.GuideEntryTitle entry={entry} />
            </h4>
            {entry.aliases.length > 0 && (
              <p class="guideAliases">also: {entry.aliases.join(", ")}</p>
            )}
            <p>{entry.description}</p>
          </div>
        ))}

        <GuideLanguagePicker />
      </div>
    );
  }
}
